'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type ShipmentMode = 'sea-fcl' | 'sea-lcl' | 'air' | 'inland' | 'customs' | '';
type IncotermsType = 'EXW' | 'FOB' | 'CIF' | 'DAP' | 'DDP' | 'CFR' | '';

interface QuoteFormData {
  // Step 1 — Shipment type
  mode: ShipmentMode;
  // Step 2 — Origin & Destination
  originCountry: string;
  originCity: string;
  destinationCountry: string;
  destinationCity: string;
  incoterms: IncotermsType;
  // Step 3 — Cargo details
  cargoDescription: string;
  hsCode: string;
  containerType: string;
  quantity: string;
  weight: string;
  volume: string;
  hazardous: boolean;
  // Step 4 — Dates & Services
  readyDate: string;
  additionalServices: string[];
  // Step 5 — Contact
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  notes: string;
}

const STEPS = [
  { id: 1, label: 'Shipment Type', icon: '🚢' },
  { id: 2, label: 'Route', icon: '🗺️' },
  { id: 3, label: 'Cargo', icon: '📦' },
  { id: 4, label: 'Services', icon: '⚙️' },
  { id: 5, label: 'Contact', icon: '👤' },
];

const MODES = [
  {
    id: 'sea-fcl',
    label: 'Sea Freight — FCL',
    desc: 'Full Container Load',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="20" width="40" height="20" rx="2" />
        <path d="M4 28h40M16 20v20M32 20v20" />
        <path d="M8 16l4-8h24l4 8" />
        <circle cx="12" cy="42" r="3" fill="currentColor" stroke="none" />
        <circle cx="36" cy="42" r="3" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: 'sea-lcl',
    label: 'Sea Freight — LCL',
    desc: 'Less than Container Load',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="20" width="40" height="20" rx="2" />
        <path d="M4 28h40M16 20v20M32 20v20" />
        <path d="M8 16l4-8h24l4 8" />
        <rect x="10" y="23" width="8" height="12" rx="1" fill="currentColor" opacity="0.3" />
        <rect x="20" y="23" width="8" height="12" rx="1" fill="currentColor" opacity="0.3" />
        <circle cx="12" cy="42" r="3" fill="currentColor" stroke="none" />
        <circle cx="36" cy="42" r="3" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: 'air',
    label: 'Air Freight',
    desc: 'Fast & reliable air cargo',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.5">
        <path d="M6 24l8-8 20 4 8-4v6l-8 2-20 4-8-4z" />
        <path d="M14 16l2 16M34 16l2 8" />
        <path d="M6 32l6-2" />
      </svg>
    ),
  },
  {
    id: 'inland',
    label: 'Inland Transport',
    desc: 'Road & rail across Europe',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="22" width="32" height="16" rx="2" />
        <path d="M34 30h8l4-8H34" />
        <circle cx="10" cy="40" r="4" />
        <circle cx="26" cy="40" r="4" />
        <circle cx="40" cy="40" r="4" />
        <path d="M2 30h32" />
      </svg>
    ),
  },
  {
    id: 'customs',
    label: 'Customs Clearance',
    desc: 'Expert customs representation',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10" stroke="currentColor" strokeWidth="1.5">
        <rect x="8" y="6" width="32" height="36" rx="2" />
        <path d="M16 16h16M16 22h16M16 28h10" />
        <circle cx="36" cy="36" r="6" fill="rgba(0,125,50,0.2)" />
        <path d="M33 36l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const CONTAINER_TYPES = ["20\' Standard", "40\' Standard", "40\' High Cube", "20\' Reefer", "40\' Reefer", "20\' Open Top", "40\' Open Top", "20\' Flat Rack", "40\' Flat Rack"];
const ADDITIONAL_SERVICES = [
  { id: 'customs', label: 'Customs Clearance' },
  { id: 'insurance', label: 'Cargo Insurance' },
  { id: 'warehousing', label: 'Warehousing' },
  { id: 'pickup', label: 'Origin Pickup' },
  { id: 'delivery', label: 'Destination Delivery' },
  { id: 'hazmat', label: 'Hazardous Goods Handling' },
];

const initialForm: QuoteFormData = {
  mode: '',
  originCountry: '',
  originCity: '',
  destinationCountry: '',
  destinationCity: '',
  incoterms: '',
  cargoDescription: '',
  hsCode: '',
  containerType: '',
  quantity: '',
  weight: '',
  volume: '',
  hazardous: false,
  readyDate: '',
  additionalServices: [],
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  notes: '',
};

export default function TransportQuotePage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<QuoteFormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof QuoteFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formStartedAt] = useState(() => Date.now());

  const update = (field: keyof QuoteFormData, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const toggleService = (id: string) => {
    setForm((prev) => ({
      ...prev,
      additionalServices: prev.additionalServices.includes(id)
        ? prev.additionalServices.filter((s) => s !== id)
        : [...prev.additionalServices, id],
    }));
  };

  const validateStep = (): boolean => {
    const newErrors: Partial<Record<keyof QuoteFormData, string>> = {};
    if (step === 1 && !form.mode) newErrors.mode = 'Please select a shipment type.';
    if (step === 2) {
      if (!form.originCountry) newErrors.originCountry = 'Required';
      if (!form.originCity) newErrors.originCity = 'Required';
      if (!form.destinationCountry) newErrors.destinationCountry = 'Required';
      if (!form.destinationCity) newErrors.destinationCity = 'Required';
    }
    if (step === 3) {
      if (!form.cargoDescription) newErrors.cargoDescription = 'Required';
      if (!form.weight) newErrors.weight = 'Required';
    }
    if (step === 5) {
      if (!form.contactName) newErrors.contactName = 'Required';
      if (!form.email) newErrors.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const next = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, 5));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          formType: 'quote-request',
          website: '',
          formStartedAt,
        }),
      });

      const responseText = await res.text();
      let json: { error?: string } = {};
      try {
        json = responseText ? JSON.parse(responseText) : {};
      } catch {
        setSubmitError(responseText || 'Empty response from mail server.');
        return;
      }

      if (!res.ok) {
        setSubmitError(json.error || responseText || 'Something went wrong. Please try again.');
        return;
      }

      setSubmitted(true);
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedMode = MODES.find((m) => m.id === form.mode);

  return (
    <>
      <Header />
      <main className="min-h-screen pt-28 pb-20 font-body" style={{ background: 'linear-gradient(160deg, #F4F6F9 0%, #EAEEF4 50%, #F4F6F9 100%)' }}>
        {/* Background decorations */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: '#1B6B8A' }} />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: '#5BBFA0' }} />
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          {/* Page header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(27,107,138,0.12)', color: '#1B6B8A', border: '1px solid rgba(27,107,138,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 0 2-2h2a2 2 0 0 0 2 2" /></svg>
              Free, no-obligation quote
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold font-display mb-3" style={{ color: '#0F2340' }}>
              Request a Transport Quote
            </h1>
            <p className="text-base" style={{ color: '#3A5070' }}>
              Tell us about your shipment and we'll get back to you within <strong style={{ color: '#1B6B8A' }}>24 hours</strong> with a tailored offer.
            </p>
          </div>

          {submitted ? (
            /* Success state */
            <div className="rounded-2xl p-10 text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)', boxShadow: '0 4px 24px rgba(15,35,64,0.08)' }}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(0,125,50,0.1)', border: '2px solid rgba(0,153,64,0.4)' }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#009940" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold font-display mb-3" style={{ color: '#0F2340' }}>Quote Request Received!</h2>
              <p className="mb-2" style={{ color: '#3A5070' }}>
                Thank you, <strong style={{ color: '#0F2340' }}>{form.contactName || 'there'}</strong>. Our team will review your shipment details and send you a personalised quote.
              </p>
              <p className="text-sm mb-8" style={{ color: '#6A85A0' }}>
                Expect a response at <strong style={{ color: '#1B6B8A' }}>{form.email}</strong> within 24 business hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => { setSubmitted(false); setStep(1); setForm(initialForm); }}
                  className="px-6 py-3 rounded-full text-sm font-semibold transition-all"
                  style={{ background: '#F4F6F9', color: '#3A5070', border: '1px solid rgba(15,35,64,0.15)' }}
                >
                  Submit another quote
                </button>
                <Link
                  href="/home-page"
                  className="px-6 py-3 rounded-full text-sm font-semibold text-white transition-all"
                  style={{ background: '#C8922A' }}
                >
                  Back to homepage
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Step indicator */}
              <div className="flex items-center justify-between mb-8 px-2">
                {STEPS.map((s, i) => (
                  <React.Fragment key={s.id}>
                    <button
                      onClick={() => s.id < step && setStep(s.id)}
                      className={`flex flex-col items-center gap-1 group transition-all ${s.id < step ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                        style={{
                          background: s.id === step ? '#1A5FAB' : s.id < step ? '#007D32' : 'rgba(255,255,255,0.12)',
                          color: s.id <= step ? '#FFFFFF' : '#6A85A0',
                          border: s.id === step ? '2px solid rgba(36,112,200,0.6)' : s.id < step ? '2px solid rgba(0,125,50,0.5)' : '2px solid rgba(255,255,255,0.15)',
                          boxShadow: s.id === step ? '0 0 16px rgba(26,95,171,0.3)' : 'none',
                        }}
                      >
                        {s.id < step ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                        ) : (
                          s.id
                        )}
                      </div>
                      <span className="hidden sm:block text-xs font-medium" style={{ color: s.id === step ? '#1B6B8A' : s.id < step ? '#009940' : '#6A85A0' }}>
                        {s.label}
                      </span>
                    </button>
                    {i < STEPS.length - 1 && (
                      <div className="flex-1 h-px mx-2 transition-all duration-500" style={{ background: i < step - 1 ? 'linear-gradient(to right, #007D32, #009940)' : 'rgba(255,255,255,0.12)' }} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Form card */}
              <form onSubmit={handleSubmit}>
                <div className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)', boxShadow: '0 4px 24px rgba(15,35,64,0.08)' }}>
                  {/* Card header */}
                  <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(15,35,64,0.08)', background: 'rgba(27,107,138,0.08)' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{STEPS[step - 1].icon}</span>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-widest mb-0.5" style={{ color: '#1B6B8A' }}>Step {step} of {STEPS.length}</p>
                        <h2 className="text-lg font-bold font-display" style={{ color: '#0F2340' }}>{STEPS[step - 1].label}</h2>
                      </div>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="p-6 sm:p-8">

                    {/* ── STEP 1: Shipment Mode ── */}
                    {step === 1 && (
                      <div>
                        <p className="text-sm mb-5" style={{ color: '#3A5070' }}>What type of shipment are you looking to move?</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {MODES.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => update('mode', m.id)}
                              className="flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-200 group"
                              style={{
                                background: form.mode === m.id ? 'rgba(27,107,138,0.12)' : '#F4F6F9',
                                border: form.mode === m.id ? '2px solid rgba(27,107,138,0.5)' : '2px solid rgba(15,35,64,0.1)',
                                boxShadow: form.mode === m.id ? '0 0 16px rgba(27,107,138,0.15)' : 'none',
                              }}
                            >
                              <div style={{ color: form.mode === m.id ? '#1B6B8A' : '#6A85A0' }}>{m.icon}</div>
                              <div>
                                <div className="font-semibold text-sm" style={{ color: form.mode === m.id ? '#0F2340' : '#3A5070' }}>{m.label}</div>
                                <div className="text-xs mt-0.5" style={{ color: form.mode === m.id ? '#1B6B8A' : '#6A85A0' }}>{m.desc}</div>
                              </div>
                              {form.mode === m.id && (
                                <div className="ml-auto w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: '#1B6B8A' }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                        {errors.mode && <p className="mt-3 text-sm" style={{ color: '#dc2626' }}>{errors.mode}</p>}
                      </div>
                    )}

                    {/* ── STEP 2: Route ── */}
                    {step === 2 && (
                      <div className="space-y-6">
                        <p className="text-sm" style={{ color: '#3A5070' }}>Where is your cargo moving from and to?</p>

                        {/* Origin */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: '#1B6B8A', color: '#FFFFFF' }}>A</div>
                            <span className="text-sm font-semibold" style={{ color: '#0F2340' }}>Origin</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Country *</label>
                              <input
                                type="text"
                                placeholder="e.g. China"
                                value={form.originCountry}
                                onChange={(e) => update('originCountry', e.target.value)}
                                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                                style={{ background: '#F4F6F9', border: errors.originCountry ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                              />
                              {errors.originCountry && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.originCountry}</p>}
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>City / Port *</label>
                              <input
                                type="text"
                                placeholder="e.g. Shanghai"
                                value={form.originCity}
                                onChange={(e) => update('originCity', e.target.value)}
                                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                                style={{ background: '#F4F6F9', border: errors.originCity ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                              />
                              {errors.originCity && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.originCity}</p>}
                            </div>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-px" style={{ background: 'rgba(15,35,64,0.1)' }} />
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(27,107,138,0.1)', border: '1px solid rgba(27,107,138,0.25)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
                          </div>
                          <div className="flex-1 h-px" style={{ background: 'rgba(15,35,64,0.1)' }} />
                        </div>

                        {/* Destination */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: '#1B6B8A', color: '#FFFFFF' }}>B</div>
                            <span className="text-sm font-semibold" style={{ color: '#0F2340' }}>Destination</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Country *</label>
                              <input
                                type="text"
                                placeholder="e.g. Bulgaria"
                                value={form.destinationCountry}
                                onChange={(e) => update('destinationCountry', e.target.value)}
                                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                                style={{ background: '#F4F6F9', border: errors.destinationCountry ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                              />
                              {errors.destinationCountry && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.destinationCountry}</p>}
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>City / Port *</label>
                              <input
                                type="text"
                                placeholder="e.g. Sofia"
                                value={form.destinationCity}
                                onChange={(e) => update('destinationCity', e.target.value)}
                                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                                style={{ background: '#F4F6F9', border: errors.destinationCity ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                              />
                              {errors.destinationCity && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.destinationCity}</p>}
                            </div>
                          </div>
                        </div>

                        {/* Incoterms */}
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Incoterms <span style={{ fontWeight: 400 }}>(optional)</span></label>
                          <select
                            value={form.incoterms}
                            onChange={(e) => update('incoterms', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all appearance-none"
                            style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: form.incoterms ? '#0F2340' : '#6A85A0' }}
                          >
                            <option value="">Select Incoterms</option>
                            {(['EXW', 'FOB', 'CIF', 'DAP', 'DDP', 'CFR'] as IncotermsType[]).map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 3: Cargo Details ── */}
                    {step === 3 && (
                      <div className="space-y-5">
                        <p className="text-sm" style={{ color: '#3A5070' }}>Help us understand what you're shipping.</p>

                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Cargo Description *</label>
                          <textarea
                            rows={3}
                            placeholder="e.g. Electronic components, furniture, machinery parts..."
                            value={form.cargoDescription}
                            onChange={(e) => update('cargoDescription', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all resize-none"
                            style={{ background: '#F4F6F9', border: errors.cargoDescription ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                          />
                          {errors.cargoDescription && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.cargoDescription}</p>}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>HS Code <span style={{ fontWeight: 400 }}>(optional)</span></label>
                            <input
                              type="text"
                              placeholder="e.g. 8471.30"
                              value={form.hsCode}
                              onChange={(e) => update('hsCode', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                          </div>
                          {(form.mode === 'sea-fcl') && (
                            <div>
                              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Container Type</label>
                              <select
                                value={form.containerType}
                                onChange={(e) => update('containerType', e.target.value)}
                                className="w-full px-4 py-3 rounded-xl text-sm outline-none appearance-none"
                                style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: form.containerType ? '#0F2340' : '#6A85A0' }}
                              >
                                <option value="">Select type</option>
                                {CONTAINER_TYPES.map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Quantity</label>
                            <input
                              type="number"
                              min="1"
                              placeholder="Units"
                              value={form.quantity}
                              onChange={(e) => update('quantity', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Weight (kg) *</label>
                            <input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={form.weight}
                              onChange={(e) => update('weight', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: errors.weight ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                            {errors.weight && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.weight}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Volume (m³)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={form.volume}
                              onChange={(e) => update('volume', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                          </div>
                        </div>

                        {/* Hazardous toggle */}
                        <button
                          type="button"
                          onClick={() => update('hazardous', !form.hazardous)}
                          className="flex items-center gap-3 w-full p-4 rounded-xl text-left transition-all"
                          style={{
                            background: form.hazardous ? 'rgba(220,38,38,0.08)' : '#F4F6F9',
                            border: form.hazardous ? '1.5px solid rgba(220,38,38,0.4)' : '1.5px solid rgba(15,35,64,0.15)',
                          }}
                        >
                          <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 transition-all" style={{ background: form.hazardous ? '#dc2626' : 'rgba(15,35,64,0.1)', border: form.hazardous ? 'none' : '1.5px solid rgba(15,35,64,0.2)' }}>
                            {form.hazardous && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>}
                          </div>
                          <div>
                            <span className="text-sm font-medium" style={{ color: form.hazardous ? '#dc2626' : '#3A5070' }}>Hazardous / Dangerous Goods</span>
                            <p className="text-xs mt-0.5" style={{ color: '#6A85A0' }}>IMDG, IATA DGR, or ADR classified cargo</p>
                          </div>
                        </button>
                      </div>
                    )}

                    {/* ── STEP 4: Dates & Services ── */}
                    {step === 4 && (
                      <div className="space-y-6">
                        <p className="text-sm" style={{ color: '#3A5070' }}>When is your cargo ready, and what additional services do you need?</p>

                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Cargo Ready Date <span style={{ fontWeight: 400 }}>(optional)</span></label>
                          <input
                            type="date"
                            value={form.readyDate}
                            onChange={(e) => update('readyDate', e.target.value)}
                            className="w-full sm:w-64 px-4 py-3 rounded-xl text-sm outline-none"
                            style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-3" style={{ color: '#6A85A0' }}>Additional Services <span style={{ fontWeight: 400 }}>(select all that apply)</span></label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {ADDITIONAL_SERVICES.map((s) => {
                              const active = form.additionalServices.includes(s.id);
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => toggleService(s.id)}
                                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all"
                                  style={{
                                    background: active ? 'rgba(0,125,50,0.08)' : '#F4F6F9',
                                    border: active ? '1.5px solid rgba(0,153,64,0.4)' : '1.5px solid rgba(15,35,64,0.12)',
                                  }}
                                >
                                  <div className="w-5 h-5 rounded flex items-center justify-center shrink-0" style={{ background: active ? '#007D32' : 'rgba(15,35,64,0.1)', border: active ? 'none' : '1.5px solid rgba(15,35,64,0.2)' }}>
                                    {active && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>}
                                  </div>
                                  <span className="text-sm" style={{ color: active ? '#007D32' : '#3A5070' }}>{s.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 5: Contact ── */}
                    {step === 5 && (
                      <div className="space-y-5">
                        <p className="text-sm" style={{ color: '#3A5070' }}>Almost done! Who should we send the quote to?</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Full Name *</label>
                            <input
                              type="text"
                              placeholder="Your name"
                              value={form.contactName}
                              onChange={(e) => update('contactName', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: errors.contactName ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                            {errors.contactName && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.contactName}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Company Name <span style={{ fontWeight: 400 }}>(optional)</span></label>
                            <input
                              type="text"
                              placeholder="Your company"
                              value={form.companyName}
                              onChange={(e) => update('companyName', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Email Address *</label>
                            <input
                              type="email"
                              placeholder="you@company.com"
                              value={form.email}
                              onChange={(e) => update('email', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: errors.email ? '1.5px solid #dc2626' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                            {errors.email && <p className="mt-1 text-xs" style={{ color: '#dc2626' }}>{errors.email}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Phone <span style={{ fontWeight: 400 }}>(optional)</span></label>
                            <input
                              type="tel"
                              placeholder="+359 ..."
                              value={form.phone}
                              onChange={(e) => update('phone', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                              style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: '#6A85A0' }}>Additional Notes <span style={{ fontWeight: 400 }}>(optional)</span></label>
                          <textarea
                            rows={3}
                            placeholder="Any special requirements, preferred carriers, or other details..."
                            value={form.notes}
                            onChange={(e) => update('notes', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
                            style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                          />
                        </div>

                        {/* Summary preview */}
                        <div className="rounded-xl p-4" style={{ background: 'rgba(27,107,138,0.06)', border: '1px solid rgba(27,107,138,0.2)' }}>
                          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#1B6B8A' }}>Your Quote Summary</p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <div>
                              <span style={{ color: '#6A85A0' }}>Mode: </span>
                              <span style={{ color: '#0F2340' }}>{selectedMode?.label || '—'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#6A85A0' }}>Route: </span>
                              <span style={{ color: '#0F2340' }}>{form.originCity && form.destinationCity ? `${form.originCity} → ${form.destinationCity}` : '—'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#6A85A0' }}>Cargo: </span>
                              <span style={{ color: '#0F2340' }}>{form.cargoDescription ? form.cargoDescription.slice(0, 30) + (form.cargoDescription.length > 30 ? '…' : '') : '—'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#6A85A0' }}>Weight: </span>
                              <span style={{ color: '#0F2340' }}>{form.weight ? `${form.weight} kg` : '—'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card footer — navigation */}
                  <div className="px-6 sm:px-8 py-5 flex items-center justify-between border-t" style={{ borderColor: 'rgba(15,35,64,0.08)', background: '#F4F6F9' }}>
                    <button
                      type="button"
                      onClick={back}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${step === 1 ? 'opacity-0 pointer-events-none' : ''}`}
                      style={{ background: '#FFFFFF', color: '#3A5070', border: '1px solid rgba(15,35,64,0.15)' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
                      Back
                    </button>

                    <div className="flex items-center gap-2">
                      {STEPS.map((s) => (
                        <div
                          key={s.id}
                          className="rounded-full transition-all duration-300"
                          style={{
                            width: s.id === step ? '20px' : '6px',
                            height: '6px',
                            background: s.id === step ? '#1A5FAB' : s.id < step ? '#007D32' : 'rgba(15,35,64,0.15)',
                          }}
                        />
                      ))}
                    </div>

                    {step < 5 ? (
                      <button
                        type="button"
                        onClick={next}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #1A5FAB, #2470C8)', boxShadow: '0 4px 16px rgba(26,95,171,0.3)' }}
                      >
                        Continue
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ background: 'linear-gradient(135deg, #007D32, #009940)', boxShadow: '0 4px 16px rgba(0,125,50,0.3)' }}
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>
                            Submitting…
                          </>
                        ) : (
                          <>
                            Submit Quote Request
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {submitError && (
                    <p className="mt-3 text-sm text-center" style={{ color: '#dc2626' }}>{submitError}</p>
                  )}
                </div>
              </form>

              {/* Trust badges */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs" style={{ color: '#6A85A0' }}>
                <div className="flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007D32" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  Your data is secure
                </div>
                <div className="flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007D32" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                  Response within 24h
                </div>
                <div className="flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007D32" strokeWidth="2"><path d="M20 6 9 17l-5-5" /></svg>
                  No obligation
                </div>
                <div className="flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007D32" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  30+ years of expertise
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
